import styled from '@emotion/styled';
import { Localisation } from '@model/common';
import { mq } from '@styles/breakpoints';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { getHTML } from '@util/common';
import { SkeletonElement, SkeletonVariant } from '@components/common/skeleton-loading';
import { TextMarkdown } from '@components/common/description';

/* ***************** *
 *       Types       *
 * ***************** */
export interface PageTitleProps {
  theme?: Theme;
  testId?: string;
  sideBarWidth?: number;
  heading: string | Localisation;
  subHeading?: string | Localisation;
  boldHeading?: boolean;
  count?: number;
  countLabel?: string | Localisation;
  className?: string;
  isGhosting?: boolean;
  isMarkdown?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div({});

interface MainPageTitleProps extends ThemeProps {
  boldHeading?: boolean;
  sideBarWidth?: number;
}
const MainPageTitle: any = styled.div(({ theme, boldHeading, sideBarWidth }: MainPageTitleProps) => ({
  ['h1']: {
    ...(theme.custom.typography.h2 as any),
    fontWeight: boldHeading ? 'bold' : 'normal',
    display: 'inline'
  },
  [mq.medium]: {
    maxWidth: `calc(100% - ${sideBarWidth || 0}px)`
  }
}));

interface HeadingProps extends ThemeProps {
  boldHeading?: boolean;
}
const Heading: any = styled.h1(({ boldHeading, theme }: HeadingProps) => ({
  ...(theme.custom.typography.h2 as any),
  fontWeight: boldHeading ? 'bold' : 'normal',
  display: 'inline'
}));

const TitleCount = styled.span(({ theme }: ThemeProps) => ({
  fontSize: '2.4rem',
  fontWeight: 'normal',
  paddingLeft: theme.custom.spacing.medium,

  [mq.large]: {
    fontSize: '3rem'
  }
}));

const SubHeading = styled.p(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any),
  marginTop: 0
}));

/* ************************ *
 *    PageTitleComponent    *
 * ************************ */

export const PageTitleComponent = (props: PageTitleProps) => {
  const t: any = useI18NextContext();
  const {
    sideBarWidth,
    heading,
    subHeading,
    count,
    countLabel,
    className,
    boldHeading = false,
    isGhosting,
    isMarkdown
  } = props;

  const getHeadingText = (text: string | Localisation) => {
    if (typeof text === 'string') {
      return t(text);
    }

    return t(text.label, text.vars);
  };

  if (isGhosting) {
    return <SkeletonElement variant={SkeletonVariant.HEADING} />;
  }

  return (
    <Container data-testid={props.testId || TestId.pageTitle.main} className={className}>
      <MainPageTitle sideBarWidth={sideBarWidth} boldHeading={boldHeading}>
        {heading && !isMarkdown && (
          <Heading boldHeading={boldHeading} dangerouslySetInnerHTML={getHTML(getHeadingText(heading))} />
        )}
        {isMarkdown && <TextMarkdown description={getHeadingText(heading)} />}
        {(!!count || count === 0) && countLabel && <TitleCount>({getHeadingText(countLabel)})</TitleCount>}
        {subHeading && <SubHeading>{getHeadingText(subHeading)}</SubHeading>}
      </MainPageTitle>
    </Container>
  );
};

export const PageTitle = withTheme(PageTitleComponent);
